import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { promptDialog } from '../components/ui/ImpulzaDialog';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  ArrowLeft,
  Boxes,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  Factory,
  FlaskConical,
  History,
  Package,
  Plus,
  RefreshCcw,
  Save,
  Trash2,
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import { getLocalYMD } from '../utils/storeLogistics';
import {
  buildProductionDateRange,
  buildProductionRequestRows,
  createEmptyWorkflowStep,
  normalizeWorkflowSteps,
} from '../utils/productionOrders';
import { getProductStockForSite } from '../utils/logisticsPlanning';
import {
  findRecipeDefinitionForProduct,
  loadRecipeBomForSite,
  loadRelationalRecipesIfAvailable,
} from '../utils/productionRecipes';
import { applyActiveProductsFilter } from '../utils/productStatus';

const EMPTY_STEP_DRAFT = {
  quantity: '',
  notes: '',
};

const fmt = (value) =>
  Number(value || 0).toLocaleString('es-AR', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const formatLongDate = (value) => {
  if (!value) return 'Sin fecha';
  const date = new Date(`${value}T12:00:00`);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('es-AR', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
  });
};

const formatDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString('es-AR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const getUserLabel = (userRow) => userRow?.full_name || userRow?.email || 'Usuario';

const generateIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return 'idem-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 10);
};

export default function ProduccionDetalle() {
  const { profile, organization } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const productId = searchParams.get('productId') || '';
  const initialDate = searchParams.get('requestedDate') || getLocalYMD(new Date());
  const siteIdParam = searchParams.get('siteId') || '';

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [savingWorkflow, setSavingWorkflow] = useState(false);
  const [savingStep, setSavingStep] = useState('');
  const [workflowDraft, setWorkflowDraft] = useState([]);
  const [workflowNotes, setWorkflowNotes] = useState('');
  const [stepDrafts, setStepDrafts] = useState({});

  const [products, setProducts] = useState([]);
  const [stockLevels, setStockLevels] = useState([]);
  const [sitios, setSitios] = useState([]);
  const [recipes, setRecipes] = useState([]);
  const [recetasJson, setRecetasJson] = useState([]);
  // FIX UX: flag de organización para aplicar merma al cálculo de costo en los steps
  const [enableMerma, setEnableMerma] = useState(false);
  const [requestLines, setRequestLines] = useState([]);
  const [workflowRow, setWorkflowRow] = useState(null);
  const [stepEntriesAll, setStepEntriesAll] = useState([]);
  const [productionsAll, setProductionsAll] = useState([]);
  const [profilesMap, setProfilesMap] = useState(new Map());
  const [focusedRequestedDate, setFocusedRequestedDate] = useState(initialDate);

  const [sourceSiteId, setSourceSiteId] = useState(() => siteIdParam || localStorage.getItem('last_sitio_origen') || '');
  const [sourceDepositId, setSourceDepositId] = useState(() => localStorage.getItem('last_deposito_origen') || '');
  const [targetSiteId, setTargetSiteId] = useState(() => siteIdParam || localStorage.getItem('last_sitio_destino') || '');
  const [targetDepositId, setTargetDepositId] = useState(() => localStorage.getItem('last_deposito_destino') || '');
  const syncedSiteParamRef = useRef('');
  const stepIdempotencyKeysRef = useRef({});

  const selectedProduct = useMemo(
    () => products.find((item) => item.id === productId) || null,
    [productId, products],
  );

  useEffect(() => {
    if (!siteIdParam || syncedSiteParamRef.current === siteIdParam) return;

    syncedSiteParamRef.current = siteIdParam;
    setSourceSiteId(siteIdParam);
    setSourceDepositId('');
    setTargetSiteId(siteIdParam);
    setTargetDepositId('');
  }, [siteIdParam]);

  const recipeCapableProductIds = useMemo(() => {
    const ids = new Set();
    recipes.forEach((recipe) => {
      if (recipe?.product_id) ids.add(recipe.product_id);
    });
    recetasJson.forEach((recipe) => {
      if (recipe?.producto_id) ids.add(recipe.producto_id);
    });
    return ids;
  }, [recipes, recetasJson]);

  const recipeOptions = useMemo(
    () =>
      products
        .filter((product) => recipeCapableProductIds.has(product.id))
        .map((product) => ({ value: product.id, label: product.name })),
    [products, recipeCapableProductIds],
  );

  const loadBaseData = useCallback(async () => {
    if (!profile?.organization_id || !productId) return;

    const orgId = profile.organization_id;
    const [productsRes, sitesRes, recetasJsonRes, stockLevelsRes] = await Promise.all([
      applyActiveProductsFilter(
        supabase
        .from('products')
        .select('id, name, cost_price, is_active, units_of_measure(abbreviation)')
        .eq('organization_id', orgId)
      )
        .order('name'),
      supabase
        .from('sitios')
        .select('id, nombre, depositos')
        .eq('organization_id', orgId)
        .eq('is_active', true)
        .order('nombre'),
      supabase
        .from('recetas')
        .select('*')
        .eq('organization_id', orgId),
      supabase
        .from('stock_levels')
        .select('product_id, site_id, quantity')
        .eq('organization_id', orgId),
    ]);
    // FIX UX: enable_recipe_merma sale del AuthContext (sin refetch).
    const orgRes = { data: organization, error: null };

    if (productsRes.error) throw productsRes.error;
    if (sitesRes.error) throw sitesRes.error;
    if (recetasJsonRes.error) throw recetasJsonRes.error;
    if (stockLevelsRes.error) throw stockLevelsRes.error;
    if (orgRes.error && orgRes.error.code !== 'PGRST204') {
      console.warn('[ProduccionDetalle] No se pudo leer enable_recipe_merma:', orgRes.error);
    }

    const recetasRows = recetasJsonRes.data || [];
    const relationalRecipes =
      recetasRows.length > 0
        ? []
        : await loadRelationalRecipesIfAvailable({
            supabase,
            organizationId: orgId,
          });

    setProducts(productsRes.data || []);
    setStockLevels(stockLevelsRes.data || []);
    setSitios(sitesRes.data || []);
    setRecipes(relationalRecipes);
    setRecetasJson(recetasRows);
    setEnableMerma(Boolean(orgRes.data?.enable_recipe_merma));
  }, [productId, profile?.organization_id]);

  const loadOperationalData = useCallback(async () => {
    if (!profile?.organization_id || !productId) return;

    const orgId = profile.organization_id;
    const firstLoad = loading;
    if (!firstLoad) setRefreshing(true);

    try {
      let requestQuery = supabase
        .from('production_request_lines')
        .select('*')
        .eq('organization_id', orgId)
        .eq('product_id', productId)
        .eq('is_active', true)
        .gte('requested_date', initialDate)
        .order('requested_date', { ascending: true })
        .order('created_at', { ascending: true });

      if (siteIdParam) {
        requestQuery = requestQuery.eq('site_id', siteIdParam);
      }

      const [requestRes, workflowRes, productsRes, stockLevelsRes] = await Promise.all([
        requestQuery,
        supabase
          .from('product_production_workflows')
          .select('*')
          .eq('organization_id', orgId)
          .eq('product_id', productId)
          .maybeSingle(),
        applyActiveProductsFilter(
          supabase
          .from('products')
          .select('id, name, cost_price, is_active, units_of_measure(abbreviation)')
          .eq('organization_id', orgId)
        )
          .order('name'),
        supabase
          .from('stock_levels')
          .select('product_id, site_id, quantity')
          .eq('organization_id', orgId),
      ]);

      if (requestRes.error) throw requestRes.error;
      if (workflowRes.error) throw workflowRes.error;
      if (productsRes.error) throw productsRes.error;
      if (stockLevelsRes.error) throw stockLevelsRes.error;

      const lines = requestRes.data || [];
      const maxRequestedDate = lines.reduce(
        (acc, line) => (String(line.requested_date || '') > acc ? String(line.requested_date || '') : acc),
        initialDate,
      );

      let stepEntriesQuery = supabase
        .from('production_step_entries')
        .select('*')
        .eq('organization_id', orgId)
        .eq('product_id', productId)
        .gte('operational_date', initialDate)
        .lte('operational_date', maxRequestedDate)
        .order('operational_date', { ascending: true })
        .order('created_at', { ascending: true });

      if (siteIdParam) {
        stepEntriesQuery = stepEntriesQuery.eq('site_id', siteIdParam);
      }

      const [stepEntriesRes, productionsRes] = await Promise.all([
        stepEntriesQuery,
        supabase
          .from('produccion')
          .select('*')
          .eq('organization_id', orgId)
          .eq('producto_id', productId)
          .gte('operational_date', initialDate)
          .lte('operational_date', maxRequestedDate)
          .order('operational_date', { ascending: true })
          .order('fecha', { ascending: true }),
      ]);

      if (stepEntriesRes.error) throw stepEntriesRes.error;
      if (productionsRes.error) throw productionsRes.error;

      setRequestLines(lines);
      setWorkflowRow(workflowRes.data || null);
      setStepEntriesAll(stepEntriesRes.data || []);
      setProductionsAll(productionsRes.data || []);
      setProducts(productsRes.data || []);
      setStockLevels(stockLevelsRes.data || []);

      const profileIds = Array.from(
        new Set(
          [...(stepEntriesRes.data || []), ...(productionsRes.data || [])]
            .flatMap((row) => [row.created_by, row.updated_by, row.deleted_by, row.anulado_por])
            .filter(Boolean),
        ),
      );

      if (profileIds.length > 0) {
        const profilesRes = await supabase
          .from('profiles')
          .select('id, full_name, email')
          .in('id', profileIds);

        if (profilesRes.error) throw profilesRes.error;
        setProfilesMap(new Map((profilesRes.data || []).map((row) => [row.id, row])));
      } else {
        setProfilesMap(new Map());
      }

      const nextSteps = normalizeWorkflowSteps(workflowRes.data?.steps);
      setWorkflowDraft(nextSteps.length > 0 ? nextSteps : [createEmptyWorkflowStep(0), createEmptyWorkflowStep(1)]);
      setWorkflowNotes(workflowRes.data?.notes || '');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [initialDate, loading, productId, profile?.organization_id, siteIdParam]);

  useEffect(() => {
    if (!profile?.organization_id || !productId) return;

    const run = async () => {
      try {
        await loadBaseData();
      } catch (error) {
        console.error(error);
        toast.error('No se pudo cargar la base del detalle.');
      }
    };

    run();
  }, [loadBaseData, productId, profile?.organization_id]);

  useEffect(() => {
    if (!profile?.organization_id || !productId || products.length === 0) return;

    const run = async () => {
      try {
        await loadOperationalData();
      } catch (error) {
        console.error(error);
        toast.error('No se pudo cargar el detalle de produccion.');
      }
    };

    run();
  }, [loadOperationalData, productId, products.length, profile?.organization_id]);

  const stockByProductId = useMemo(() => {
    const map = new Map();

    (stockLevels || []).forEach((level) => {
      const productId = level?.product_id;
      if (!productId) return;
      if (siteIdParam && level.site_id !== siteIdParam) return;

      map.set(productId, Number(map.get(productId) || 0) + Number(level.quantity || 0));
    });

    return map;
  }, [siteIdParam, stockLevels]);

  const activeRows = useMemo(
    () =>
      buildProductionRequestRows({
        requestLines,
        workflows: workflowRow ? [workflowRow] : [],
        stepEntries: stepEntriesAll.filter((entry) => entry.is_active),
        productions: productionsAll.filter((row) => row.activo),
        products,
        selectedSiteId: siteIdParam,
        stockByProductId,
      }),
    [products, productionsAll, requestLines, siteIdParam, stepEntriesAll, stockByProductId, workflowRow],
  );

  const row = activeRows[0] || null;

  useEffect(() => {
    if (!row?.requestedDates?.length) return;
    setFocusedRequestedDate((current) => (row.requestedDates.includes(current) ? current : row.requestedDates[0]));
  }, [row]);

  const focusedMetrics = row?.getMetricsForDate(focusedRequestedDate) || null;
  const sourceSite = sitios.find((site) => site.id === sourceSiteId) || null;
  const targetSite = sitios.find((site) => site.id === targetSiteId) || null;
  const sourceDeposits = Array.isArray(sourceSite?.depositos) ? sourceSite.depositos : [];
  const targetDeposits = Array.isArray(targetSite?.depositos) ? targetSite.depositos : [];

  const stepHistory = useMemo(() => {
    const dateEntries = stepEntriesAll.filter((entry) => entry.operational_date === focusedRequestedDate);
    return dateEntries.sort((left, right) => new Date(right.created_at).getTime() - new Date(left.created_at).getTime());
  }, [focusedRequestedDate, stepEntriesAll]);

  const legacyProductionHistory = useMemo(
    () =>
      productionsAll
        .filter(
          (productionRow) =>
            productionRow.operational_date === focusedRequestedDate &&
            !productionRow.workflow_step_entry_id,
        )
        .sort((left, right) => new Date(right.fecha).getTime() - new Date(left.fecha).getTime()),
    [focusedRequestedDate, productionsAll],
  );

  const movementHistory = useMemo(
    () =>
      [
        ...stepHistory.map((entry) => ({
          id: entry.id,
          type: 'step',
          label: entry.step_name || 'Paso',
          quantity: Number(entry.quantity || 0),
          createdAt: entry.created_at,
          active: !!entry.is_active,
          createdBy: entry.created_by,
          deletedAt: entry.deleted_at,
          deletedBy: entry.deleted_by,
          deleteReason: entry.delete_reason,
          notes: entry.notes || '',
          sourceSiteId: entry.source_site_id,
          targetSiteId: entry.target_site_id,
          sourceDepositId: entry.source_deposito_id,
          targetDepositId: entry.target_deposito_id,
          consumedItems: Array.isArray(entry.metadata?.consumed_items) ? entry.metadata.consumed_items : [],
          raw: entry,
        })),
        ...legacyProductionHistory.map((entry) => ({
          id: entry.id,
          type: 'production',
          label: entry.producto_nombre || 'Produccion',
          quantity: Number(entry.cantidad || 0),
          createdAt: entry.fecha,
          active: !!entry.activo,
          createdBy: entry.created_by,
          deletedAt: entry.anulado_el,
          deletedBy: entry.anulado_por,
          deleteReason: entry.anulacion_motivo,
          notes: entry.observaciones || '',
          sourceSiteId: entry.source_site_id,
          targetSiteId: entry.sitio_id,
          sourceDepositId: entry.source_deposito_id,
          targetDepositId: entry.deposito_id,
          consumedItems: Array.isArray(entry.ingredientes_usados) ? entry.ingredientes_usados : [],
          raw: entry,
        })),
      ].sort((left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime()),
    [legacyProductionHistory, stepHistory],
  );

  const handleWorkflowStepChange = (index, field, value) => {
    setWorkflowDraft((current) =>
      current.map((step, stepIndex) => {
        if (stepIndex !== index) return step;
        if (field === 'recipeProductId') {
          const option = recipeOptions.find((candidate) => candidate.value === value);
          return {
            ...step,
            recipeProductId: value,
            recipeProductName: option?.label || '',
          };
        }

        return {
          ...step,
          [field]: value,
        };
      }),
    );
  };

  const handleAddWorkflowStep = () => {
    setWorkflowDraft((current) => [...current, createEmptyWorkflowStep(current.length)]);
  };

  const handleRemoveWorkflowStep = (index) => {
    const stepToRemove = workflowDraft[index];
    if (!stepToRemove) return;

    const hasEntries = stepEntriesAll.some((entry) => entry.step_key === stepToRemove.key && entry.is_active);
    if (hasEntries) {
      toast.error('No puedes quitar un paso que ya tiene avances activos.');
      return;
    }

    setWorkflowDraft((current) => current.filter((_, stepIndex) => stepIndex !== index));
  };

  const handleSaveWorkflow = async () => {
    if (!profile?.organization_id || !row?.productId) return;

    const normalized = normalizeWorkflowSteps(workflowDraft).map((step, index) => ({
      ...step,
      key: step.key || `paso-${index + 1}`,
      name: step.name || `Paso ${index + 1}`,
    }));

    if (normalized.length === 0) return toast.error('Agrega al menos un paso.');

    const validKeys = new Set(normalized.map((step) => step.key));
    const orphanedEntries = stepEntriesAll.filter((entry) => entry.is_active && !validKeys.has(entry.step_key));
    if (orphanedEntries.length > 0) {
      return toast.error('Hay avances activos en pasos que intentas quitar.');
    }

    setSavingWorkflow(true);

    try {
      const { error } = await supabase.from('product_production_workflows').upsert(
        {
          organization_id: profile.organization_id,
          product_id: row.productId,
          steps: normalized,
          notes: workflowNotes || '',
          is_active: true,
        },
        { onConflict: 'organization_id,product_id' },
      );

      if (error) throw error;

      toast.success('Flujo guardado.');
      await loadOperationalData();
    } catch (error) {
      console.error(error);
      toast.error('No se pudo guardar el flujo.');
    } finally {
      setSavingWorkflow(false);
    }
  };

  const handleStepDraftChange = (stepKey, field, value) => {
    setStepDrafts((current) => ({
      ...current,
      [stepKey]: {
        ...(current[stepKey] || EMPTY_STEP_DRAFT),
        [field]: value,
      },
    }));
  };

  const persistLastLocations = () => {
    // FIX: namespacear por organización para evitar arrastrar IDs entre orgs.
    // Mantenemos las claves legacy como fallback de lectura (no escritura).
    const orgId = profile?.organization_id || 'noorg';
    const ns = (k) => `impulza:${orgId}:${k}`;
    try {
      localStorage.setItem(ns('last_sitio_origen'), sourceSiteId || '');
      localStorage.setItem(ns('last_deposito_origen'), sourceDepositId || '');
      localStorage.setItem(ns('last_sitio_destino'), targetSiteId || '');
      localStorage.setItem(ns('last_deposito_destino'), targetDepositId || '');
    } catch {
      // ignorar errores de cuota
    }
  };

  /**
   * FIX CRÍTICO #3: cierre automático del flujo cuando todos los pasos están completos.
   *
   * Cambios respecto de la versión anterior (que infla producción al haber multi-pedido):
   *  1. Filtrar `production_step_entries` y `produccion` por `request_line_ids` del pedido
   *     en foco (NO solo por operational_date+product_id), para no mezclar avances de
   *     otros pedidos del mismo producto y fecha en otros sitios.
   *  2. Idempotency_key determinístico (mismo conjunto de request_line_ids → mismo key).
   *     Si el RPC del paso final ya creó el `produccion`, esta segunda llamada se rechaza
   *     server-side y devolvemos false silenciosamente. Requiere índice único en
   *     `produccion.idempotency_key` (ver migración SQL adjunta).
   *  3. Si el caller acaba de registrar el step final (skipIfFinalStepJustRegistered=true)
   *     asumimos que el RPC del paso final ya hizo el ingreso a stock y no volvemos a
   *     llamar `register_production_entry`. Esto evita el doble cierre incluso sin
   *     idempotencia server-side. Mantener ambos mecanismos como defensa en profundidad.
   */
  const finalizeCompletedFlowIfNeeded = async ({ skipIfFinalStepJustRegistered = false } = {}) => {
    if (!profile?.organization_id || !row || !focusedMetrics || row.steps.length === 0) return false;

    if (skipIfFinalStepJustRegistered) {
      // El paso final acaba de ser registrado: el RPC `register_production_step_entry`
      // con is_final_step=true es el responsable único de crear el registro en `produccion`.
      // Salir acá evita el doble ingreso a stock.
      return false;
    }

    const focusedLineIds = focusedMetrics.lines.map((line) => line.id).filter(Boolean);
    if (focusedLineIds.length === 0) {
      // Sin líneas vinculadas no podemos garantizar aislamiento entre pedidos → no cerramos
      console.warn('[finalizeCompletedFlowIfNeeded] focusedMetrics.lines vacío; cierre automático abortado.');
      return false;
    }

    // Filtramos por request_line_ids para AISLAR este pedido del resto
    const { data: freshStepEntries, error: stepEntriesError } = await supabase
      .from('production_step_entries')
      .select('step_key, quantity, source_site_id, source_deposito_id, target_site_id, target_deposito_id, request_line_ids')
      .eq('organization_id', profile.organization_id)
      .eq('product_id', row.productId)
      .eq('operational_date', focusedRequestedDate)
      .eq('is_active', true)
      .overlaps('request_line_ids', focusedLineIds);

    if (stepEntriesError) throw stepEntriesError;

    const { data: freshProductions, error: productionsError } = await supabase
      .from('produccion')
      .select('cantidad, production_request_line_ids')
      .eq('organization_id', profile.organization_id)
      .eq('producto_id', row.productId)
      .eq('operational_date', focusedRequestedDate)
      .eq('activo', true)
      .overlaps('production_request_line_ids', focusedLineIds);

    if (productionsError) throw productionsError;

    const stepTotals = new Map();
    (freshStepEntries || []).forEach((entry) => {
      stepTotals.set(
        entry.step_key,
        Number(stepTotals.get(entry.step_key) || 0) + Number(entry.quantity || 0),
      );
    });

    const flowCompletedQuantity = Math.min(
      ...row.steps.map((stepItem) => Number(stepTotals.get(stepItem.key) || 0)),
    );
    const finalizedQuantity = (freshProductions || []).reduce(
      (acc, entry) => acc + Number(entry.cantidad || 0),
      0,
    );
    const delta = Math.max(flowCompletedQuantity - finalizedQuantity, 0);

    if (delta <= 0) return false;

    const firstSourceEntry = (freshStepEntries || []).find((entry) => entry.source_site_id);
    const firstTargetEntry = (freshStepEntries || []).find((entry) => entry.target_site_id);
    const resolvedTargetSiteId = targetSiteId || firstTargetEntry?.target_site_id || siteIdParam || row.primarySiteId || sourceSiteId || firstSourceEntry?.source_site_id || null;
    const resolvedSourceSiteId = sourceSiteId || firstSourceEntry?.source_site_id || resolvedTargetSiteId;

    if (!resolvedSourceSiteId || !resolvedTargetSiteId) {
      throw new Error('Falta definir sitio origen o destino para cerrar automaticamente la produccion.');
    }

    // Clave determinística: mismos request_line_ids + fecha → mismo idempotency_key
    // Si se llama dos veces (race condition), el RPC rechaza el segundo
    const sortedLineIds = [...focusedLineIds].sort().join(',');
    const idempotencyKey = `auto-flow:${row.productId}:${focusedRequestedDate}:${sortedLineIds}`;

    const payload = {
      organization_id: profile.organization_id,
      product_id: row.productId,
      product_name: row.name,
      quantity: delta,
      source_site_id: resolvedSourceSiteId,
      source_deposito_id: sourceDepositId || firstSourceEntry?.source_deposito_id || null,
      target_site_id: resolvedTargetSiteId,
      target_deposito_id: targetDepositId || firstTargetEntry?.target_deposito_id || null,
      ingredientes_usados: [],
      observaciones: `Cierre automatico del flujo ${focusedRequestedDate}`,
      cost_unitario: Number(selectedProduct?.cost_price || 0),
      cost_total: Number(selectedProduct?.cost_price || 0) * delta,
      operational_date: focusedRequestedDate,
      source_type: 'pedido_produccion',
      production_request_line_ids: focusedLineIds,
      idempotency_key: idempotencyKey,
    };

    const rpcRes = await supabase.rpc('register_production_entry', {
      p_payload: payload,
    });

    if (rpcRes.error) {
      // Si el error es por duplicación de idempotency_key, lo tomamos como éxito (otro proceso ya cerró)
      const msg = String(rpcRes.error?.message || '').toLowerCase();
      if (msg.includes('idempotency') || msg.includes('duplicate')) {
        console.info('[finalizeCompletedFlowIfNeeded] Cierre ya realizado (idempotency).');
        return false;
      }
      throw rpcRes.error;
    }

    return true;
  };

  const handleRegisterStep = async (step) => {
    if (!profile?.organization_id || !row || !focusedMetrics) return;

    const draft = stepDrafts[step.key] || EMPTY_STEP_DRAFT;
    const quantity = Number(draft.quantity || 0);
    if (!Number.isFinite(quantity) || quantity <= 0) return toast.error('Ingresa una cantidad valida.');

    setSavingStep(step.key);

    try {
      let recipeData = {
        hasRecipe: false,
        recipe: null,
        bom: [],
        costTotal: 0,
        costUnit: 0,
      };
      const stepSourceSiteId =
        step.siteId ||
        step.source_site_id ||
        sourceSiteId ||
        row?.primarySiteId ||
        siteIdParam ||
        null;

      if (step.recipeProductId) {
        // FIX IMPORTANTE #12: el sitio donde se descuenta la materia prima del paso
        // puede ser distinto del global de la UI. Prioridad:
        //   1) step.siteId (configurado en el flujo, ej. "empaquetado en planta B")
        //   2) sourceSiteId (selección global del usuario)
        //   3) primarySiteId del pedido / siteIdParam de la URL
        // Antes solo se usaba sourceSiteId → falsos "stock insuficiente" cuando la
        // bolsa/etiqueta vivía en otro sitio.
        if (!stepSourceSiteId) {
          throw new Error('Seleccioná el sitio origen para descontar la materia prima del paso.');
        }

        recipeData = await loadRecipeBomForSite({
          supabase,
          productId: step.recipeProductId,
          siteId: stepSourceSiteId,
          quantity,
          recetasJson,
          recipes,
          // FIX UX: si la org activó "Merma Esperada en Recetas", el costo unitario
          // del step final se ajusta por la merma esperada de la sub-receta.
          applyMerma: enableMerma,
        });

        if (!recipeData.hasRecipe) {
          throw new Error('El paso tiene receta asignada pero no encontramos su BOM.');
        }

        const faltantes = recipeData.bom.filter((item) => item.falta);
        if (faltantes.length > 0) {
          const faltante = faltantes[0];
          const stepSourceSite = sitios.find((site) => site.id === stepSourceSiteId) || sourceSite;
          const siteLabel = stepSourceSite?.nombre || 'el sitio origen seleccionado';
          throw new Error(
            `Stock insuficiente para ${faltante.nombre} en ${siteLabel}. Disponible ${fmt(faltante.stockActual)}, necesario ${fmt(faltante.necesario)}.`,
          );
        }
      }

      if (step.isFinalStep && !targetSiteId) {
        throw new Error('Selecciona el sitio destino para sumar la produccion finalizada.');
      }

      const idempotencyKeyRefKey = `${step.key}::${focusedRequestedDate}`;
      if (!stepIdempotencyKeysRef.current[idempotencyKeyRefKey]) {
        stepIdempotencyKeysRef.current[idempotencyKeyRefKey] = generateIdempotencyKey();
      }

      const payload = {
        organization_id: profile.organization_id,
        product_id: row.productId,
        product_name: row.name,
        site_id: siteIdParam || row.primarySiteId || targetSiteId || sourceSiteId || null,
        step_key: step.key,
        step_name: step.name,
        step_type: step.type,
        quantity,
        operational_date: focusedRequestedDate,
        notes: draft.notes || '',
        source_type: 'pedido_produccion',
        source_site_id: stepSourceSiteId || null,
        source_deposito_id: (step.siteId && step.siteId !== sourceSiteId) ? null : sourceDepositId || null,
        target_site_id: step.isFinalStep ? targetSiteId || null : null,
        target_deposito_id: step.isFinalStep ? targetDepositId || null : null,
        recipe_product_id: step.recipeProductId || null,
        recipe_product_name: step.recipeProductName || null,
        request_line_ids: focusedMetrics.lines.map((line) => line.id),
        consumed_items: recipeData.bom,
        cost_unitario: step.isFinalStep ? recipeData.costUnit : 0,
        cost_total: step.isFinalStep ? recipeData.costTotal : 0,
        is_final_step: step.isFinalStep,
        metadata: {
          product_name: row.name,
          requested_date: focusedRequestedDate,
          step_position: step.position,
        },
        idempotency_key: stepIdempotencyKeysRef.current[idempotencyKeyRefKey],
      };

      const rpcRes = await supabase.rpc('register_production_step_entry', {
        p_payload: payload,
      });

      if (rpcRes.error) throw rpcRes.error;
      delete stepIdempotencyKeysRef.current[idempotencyKeyRefKey];

      // FIX CRÍTICO #3: si el step recién registrado era el final, el RPC ya hizo el ingreso a stock.
      // Saltamos el cierre automático para evitar doble ingreso. El cierre automático queda
      // solo para los casos donde el último avance NO fue el step final (ej. usuario carga
      // un step intermedio que recién ahora completa el flujo).
      const autoFinalized = await finalizeCompletedFlowIfNeeded({
        skipIfFinalStepJustRegistered: !!step.isFinalStep,
      });
      persistLastLocations();
      setStepDrafts((current) => ({
        ...current,
        [step.key]: EMPTY_STEP_DRAFT,
      }));
      toast.success(
        step.isFinalStep || autoFinalized
          ? 'Produccion finalizada y stock actualizado.'
          : 'Avance registrado.',
      );
      await loadOperationalData();
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'No se pudo registrar el avance.');
    } finally {
      setSavingStep('');
    }
  };

  const handleReverseStepEntry = async (entry) => {
    const reason = await promptDialog({
      title: 'Anular movimiento',
      message: 'Se revierte este movimiento de la producción.',
      label: 'Motivo de anulación',
      defaultValue: 'Carga incorrecta',
      required: true,
      tone: 'danger',
      confirmLabel: 'Anular',
    });
    if (reason === null) return;

    setSavingStep(entry.id);

    try {
      const rpcRes = await supabase.rpc('reverse_production_step_entry', {
        p_step_entry_id: entry.id,
        p_reason: reason,
      });

      if (rpcRes.error) throw rpcRes.error;

      toast.success('Movimiento revertido.');
      await loadOperationalData();
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'No se pudo revertir el movimiento.');
    } finally {
      setSavingStep('');
    }
  };

  const handleReverseLegacyProduction = async (productionRow) => {
    const reason = await promptDialog({
      title: 'Anular producción',
      message: 'Se revierte la producción: vuelve el stock de insumos y se descuenta el producto terminado.',
      label: 'Motivo de anulación',
      defaultValue: 'Carga incorrecta',
      required: true,
      tone: 'danger',
      confirmLabel: 'Anular',
    });
    if (reason === null) return;

    setSavingStep(productionRow.id);

    try {
      const rpcRes = await supabase.rpc('reverse_production_entry', {
        p_production_id: productionRow.id,
        p_reason: reason,
      });

      if (rpcRes.error) throw rpcRes.error;

      const warnings = Array.isArray(rpcRes.data?.warnings) ? rpcRes.data.warnings.filter(Boolean) : [];
      if (warnings.length > 0) {
        toast(`Produccion revertida con advertencias: ${warnings.join(' ')}`, { icon: '!', duration: 7000 });
      } else {
        toast.success('Produccion revertida.');
      }
      await loadOperationalData();
    } catch (error) {
      console.error(error);
      toast.error(error.message || 'No se pudo revertir la produccion.');
    } finally {
      setSavingStep('');
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={36} />
        <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">
          Cargando detalle de produccion...
        </p>
      </div>
    );
  }

  if (!productId || !selectedProduct || !row || !focusedMetrics) {
    return (
      <div className="imp-page imp-container animate-nova-in">
        <div className="imp-header mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={() => navigate(`/sistema/pedidos-produccion?date=${initialDate}${siteIdParam ? `&siteId=${siteIdParam}` : ''}`)}
              className="w-10 h-10 rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm hover:border-[#51A7FD] transition-all flex items-center justify-center shrink-0"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-xl font-black text-slate-800 uppercase tracking-tight">Detalle de produccion</h1>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                No encontramos un producto valido para abrir el detalle.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="imp-page imp-container animate-nova-in">
      <div className="imp-header mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={() => navigate(`/sistema/pedidos-produccion?date=${initialDate}${siteIdParam ? `&siteId=${siteIdParam}` : ''}`)}
            className="w-10 h-10 rounded-2xl border border-slate-200 bg-white text-slate-600 shadow-sm hover:border-[#51A7FD] transition-all flex items-center justify-center shrink-0"
          >
            <ArrowLeft size={16} />
          </button>
          <div className="w-10 h-10 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
            <Factory size={18} className="text-[#3FF2AA]" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">{row.name}</h1>
            <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
              Detalle operativo, avances por paso y log de movimientos
            </p>
          </div>
        </div>

        <div className="imp-header-actions">
          <div className="flex items-center gap-2 px-3 py-2 rounded-2xl bg-white border border-slate-200 shadow-sm">
            <CalendarDays size={14} className="text-slate-400" />
            <select
              value={focusedRequestedDate}
              onChange={(event) => setFocusedRequestedDate(event.target.value)}
              className="bg-transparent text-[10px] font-black uppercase tracking-widest text-slate-600 outline-none cursor-pointer"
            >
              {row.requestedDates.map((date) => (
                <option key={date} value={date}>
                  {formatLongDate(date)}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            onClick={loadOperationalData}
            disabled={refreshing}
            className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
          >
            <RefreshCcw size={14} className={refreshing ? 'animate-spin' : ''} /> Actualizar
          </button>
        </div>
      </div>

      <div className="imp-kpis mb-3">
        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pedido</p>
            <ClipboardList size={16} className="text-[#51A7FD]" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{fmt(focusedMetrics.pedido)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{formatLongDate(focusedRequestedDate)}</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Pendiente de producir</p>
            <AlertTriangle size={16} className={focusedMetrics.pendingProduction > 0 ? 'text-amber-500' : 'text-emerald-500'} />
          </div>
          <p className={`mt-1.5 text-2xl font-black tracking-tight ${focusedMetrics.pendingProduction > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
            {fmt(focusedMetrics.pendingProduction)}
          </p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Objetivo segun pedido y stock</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Produccion finalizada</p>
            <CheckCircle2 size={16} className="text-emerald-500" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{fmt(focusedMetrics.finalCompleted)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Ya impacto en stock</p>
        </div>

        <div className="imp-kpi">
          <div className="flex items-center justify-between">
            <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Stock actual</p>
            <Boxes size={16} className="text-slate-400" />
          </div>
          <p className="mt-1.5 text-2xl font-black text-slate-800 tracking-tight">{fmt(row.stock)}</p>
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Disponible hoy</p>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,_1.1fr)_420px] gap-4">
        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Flujo activo</p>
                <h2 className="mt-1 text-sm font-black uppercase tracking-tight text-slate-800">Resumen del producto</h2>
              </div>
              <button
                type="button"
                onClick={() => navigate(`/sistema/produccion-flujos?productId=${row.productId}`)}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-2.5 text-[10px] font-black uppercase tracking-widest text-slate-600 transition-all hover:border-[#51A7FD] hover:text-[#0B2A36]"
              >
                Editar flujo
              </button>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {row.steps.length > 0 ? (
                row.steps.map((step) => (
                  <span
                    key={step.key}
                    className="inline-flex items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-600"
                  >
                    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white border border-slate-200 text-[9px]">
                      {step.position}
                    </span>
                    {step.name}
                    {step.recipeProductName ? ` · ${step.recipeProductName}` : ''}
                  </span>
                ))
              ) : (
                <span className="inline-flex items-center rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2 text-[10px] font-black uppercase tracking-widest text-amber-700">
                  Sin flujo configurado
                </span>
              )}
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-bold text-slate-500">
              La configuracion del flujo ahora se administra desde la pantalla de flujos de productos.
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Pedido del dia</p>
            <div className="mt-3 space-y-3">
              {focusedMetrics.lines.length > 0 ? (
                focusedMetrics.lines.map((line) => (
                  <div key={line.id} className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-[11px] font-black uppercase tracking-tight text-slate-800">
                          {line.metadata?.tracking_code ? `#${line.metadata.tracking_code}` : 'Carga manual'}
                        </p>
                        <p className="mt-1 text-[10px] font-bold text-slate-500">
                          {line.metadata?.customer_name || line.notes || 'Sin detalle adicional'}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Cantidad</p>
                        <p className="mt-1 text-sm font-black text-slate-800">{fmt(line.quantity)}</p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-[10px] font-bold text-slate-400">No hay lineas activas para esta fecha.</p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            {focusedMetrics.steps.length > 0 ? (
              focusedMetrics.steps.map((step) => {
                const draft = stepDrafts[step.key] || EMPTY_STEP_DRAFT;
                const recipeDefinition = step.recipeProductId
                  ? findRecipeDefinitionForProduct({
                      productId: step.recipeProductId,
                      recetasJson,
                      recipes,
                    })
                  : null;

                return (
                  <div key={step.key} className="rounded-[26px] border border-slate-200 bg-white p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                          Paso {step.position} {step.isFinalStep ? '- Cierre de produccion' : ''}
                        </p>
                        <h3 className="mt-1 text-sm font-black uppercase tracking-tight text-slate-800">{step.name}</h3>
                        <div className="mt-2 flex flex-wrap items-center gap-2">
                          <span
                            className={`px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border ${
                              step.isComplete
                                ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                                : 'bg-amber-50 border-amber-200 text-amber-700'
                            }`}
                          >
                            {step.completed >= step.target ? 'Completo' : `Avance ${fmt(step.completed)} / ${fmt(step.target)}`}
                          </span>
                          <span className="px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border border-slate-200 bg-slate-50 text-slate-600">
                            {step.type}
                          </span>
                          {step.recipeProductName ? (
                            <span className="px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border border-blue-200 bg-blue-50 text-[#51A7FD]">
                              Receta: {step.recipeProductName}
                            </span>
                          ) : (
                            <span className="px-3 py-1 rounded-xl text-[9px] font-black uppercase tracking-widest border border-slate-200 bg-slate-50 text-slate-500">
                              Sin receta
                            </span>
                          )}
                        </div>
                      </div>

                      <div
                        className={`w-11 h-11 rounded-2xl border-2 flex items-center justify-center text-sm font-black ${
                          step.isComplete ? 'border-emerald-500 bg-emerald-500 text-white' : 'border-slate-200 bg-white text-slate-400'
                        }`}
                      >
                        {step.position}
                      </div>
                    </div>

                    {step.recipeProductId && !recipeDefinition ? (
                      <div className="mt-4 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-[10px] font-bold text-rose-700">
                        La receta asignada no existe o no pudo cargarse.
                      </div>
                    ) : null}

                    {step.recipeProductId && recipeDefinition ? (
                      <div className="mt-4 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 text-[10px] font-bold text-[#205EA5]">
                        Al registrar este paso se desconta la materia prima de la receta asignada segun la cantidad informada.
                      </div>
                    ) : null}

                    {step.isFinalStep ? (
                      <div className="mt-4 rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-[10px] font-bold text-emerald-800">
                        Este paso suma stock al deposito destino y deja el registro final en la tabla de produccion.
                      </div>
                    ) : null}

                    <div className="mt-4 grid grid-cols-1 gap-3 md:grid-cols-[130px_minmax(0,_1fr)_auto]">
                      <input
                        type="number"
                        min="0"
                        step="any"
                        value={draft.quantity}
                        onChange={(event) => handleStepDraftChange(step.key, 'quantity', event.target.value)}
                        placeholder="Cantidad"
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD]"
                      />
                      <input
                        type="text"
                        value={draft.notes}
                        onChange={(event) => handleStepDraftChange(step.key, 'notes', event.target.value)}
                        placeholder="Detalle del avance"
                        className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-bold text-slate-600 outline-none focus:border-[#51A7FD]"
                      />
                      <button
                        type="button"
                        onClick={() => handleRegisterStep(step)}
                        disabled={savingStep === step.key}
                        className="rounded-2xl bg-[#3FF2AA] px-4 py-3 text-[10px] font-black uppercase tracking-widest text-[#0B2A36] transition-all disabled:opacity-60"
                      >
                        {savingStep === step.key ? 'Guardando...' : step.isFinalStep ? 'Finalizar' : 'Registrar'}
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="rounded-[26px] border border-amber-200 bg-amber-50 p-4 text-[11px] font-bold text-amber-800">
                Este producto todavia no tiene flujo configurado. Agrega pasos arriba para empezar a registrar la produccion.
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Ruteo de stock</p>
                <h2 className="mt-1 text-sm font-black uppercase tracking-tight text-slate-800">Depositos por defecto</h2>
              </div>
              <FlaskConical size={16} className="text-[#51A7FD]" />
            </div>

            <div className="mt-4 space-y-3">
              <div>
                <p className="mb-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400">Sitio origen</p>
                <select
                  value={sourceSiteId}
                  onChange={(event) => {
                    setSourceSiteId(event.target.value);
                    setSourceDepositId('');
                  }}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD]"
                >
                  <option value="">Seleccionar sitio...</option>
                  {sitios.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className="mb-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400">Deposito origen</p>
                <select
                  value={sourceDepositId}
                  onChange={(event) => setSourceDepositId(event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD]"
                >
                  <option value="">Sin deposito especifico</option>
                  {sourceDeposits.map((deposito) => (
                    <option key={String(deposito?.id || deposito?.nombre || deposito)} value={String(deposito?.id || deposito?.nombre || deposito)}>
                      {deposito?.nombre || deposito?.id || String(deposito)}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className="mb-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400">Sitio destino</p>
                <select
                  value={targetSiteId}
                  onChange={(event) => {
                    setTargetSiteId(event.target.value);
                    setTargetDepositId('');
                  }}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD]"
                >
                  <option value="">Seleccionar sitio...</option>
                  {sitios.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.nombre}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <p className="mb-1.5 text-[9px] font-black uppercase tracking-widest text-slate-400">Deposito destino</p>
                <select
                  value={targetDepositId}
                  onChange={(event) => setTargetDepositId(event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[11px] font-black text-slate-700 outline-none focus:border-[#51A7FD]"
                >
                  <option value="">Sin deposito especifico</option>
                  {targetDeposits.map((deposito) => (
                    <option key={String(deposito?.id || deposito?.nombre || deposito)} value={String(deposito?.id || deposito?.nombre || deposito)}>
                      {deposito?.nombre || deposito?.id || String(deposito)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-bold text-slate-500">
              Los ultimos depositos elegidos quedan guardados y se reutilizan en la siguiente carga de produccion.
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Resumen operativo</p>
                <h2 className="mt-1 text-sm font-black uppercase tracking-tight text-slate-800">Estado del pedido</h2>
              </div>
              <ClipboardList size={16} className="text-slate-400" />
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Unidades pedidas</p>
                <p className="mt-1 text-lg font-black text-slate-800">{fmt(focusedMetrics.pedido)}</p>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3">
                <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">Pendiente</p>
                <p className={`mt-1 text-lg font-black ${focusedMetrics.pendingProduction > 0 ? 'text-amber-600' : 'text-emerald-600'}`}>
                  {fmt(focusedMetrics.pendingProduction)}
                </p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              {focusedMetrics.steps.map((step) => (
                <div
                  key={step.key}
                  className={`inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-[10px] font-black uppercase tracking-widest ${
                    step.isComplete
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                      : 'border-slate-200 bg-slate-50 text-slate-500'
                  }`}
                >
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white border border-current/20 text-[9px]">
                    {step.position}
                  </span>
                  {step.name}
                </div>
              ))}
            </div>

            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-[10px] font-bold text-slate-500">
              Unidad base: {selectedProduct.units_of_measure?.abbreviation || 'un'}.
            </div>
          </div>

          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Historial</p>
                <h2 className="mt-1 text-sm font-black uppercase tracking-tight text-slate-800">Movimientos y auditoria</h2>
              </div>
              <History size={16} className="text-slate-400" />
            </div>

            <div className="mt-4 space-y-3">
              {movementHistory.length > 0 ? (
                movementHistory.map((movement) => {
                  const sourceSiteName = movement.sourceSiteId
                    ? sitios.find((site) => site.id === movement.sourceSiteId)?.nombre || 'Sitio origen'
                    : '';
                  const targetSiteName = movement.targetSiteId
                    ? sitios.find((site) => site.id === movement.targetSiteId)?.nombre || 'Sitio destino'
                    : '';

                  return (
                    <div key={`${movement.type}-${movement.id}`} className="rounded-2xl border border-slate-200 bg-slate-50 p-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="text-[10px] font-black uppercase tracking-widest text-slate-800">{movement.label}</span>
                            <span
                              className={`rounded-full px-2.5 py-1 text-[8px] font-black uppercase tracking-widest ${
                                movement.type === 'step'
                                  ? 'bg-blue-100 text-[#205EA5]'
                                  : 'bg-emerald-100 text-emerald-700'
                              }`}
                            >
                              {movement.type === 'step' ? 'Paso' : 'Produccion'}
                            </span>
                            <span
                              className={`rounded-full px-2.5 py-1 text-[8px] font-black uppercase tracking-widest ${
                                movement.active ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-600'
                              }`}
                            >
                              {movement.active ? 'Activo' : 'Anulado'}
                            </span>
                          </div>
                          <p className="mt-1 text-[10px] font-bold text-slate-500">
                            {fmt(movement.quantity)} u. · {formatDateTime(movement.createdAt)} · {getUserLabel(profilesMap.get(movement.createdBy))}
                          </p>
                          {sourceSiteName || targetSiteName ? (
                            <p className="mt-1 text-[10px] font-bold text-slate-500">
                              {sourceSiteName || 'Sin origen'} {movement.sourceDepositId ? `(${movement.sourceDepositId})` : ''}
                              {' -> '}
                              {targetSiteName || 'Sin destino'} {movement.targetDepositId ? `(${movement.targetDepositId})` : ''}
                            </p>
                          ) : null}
                          {movement.notes ? (
                            <p className="mt-1 text-[10px] font-bold text-slate-500">{movement.notes}</p>
                          ) : null}
                          {movement.consumedItems.length > 0 ? (
                            <p className="mt-1 text-[10px] font-bold text-slate-500">
                              Materia prima descontada: {movement.consumedItems.map((item) => item.nombre).filter(Boolean).slice(0, 3).join(', ')}
                              {movement.consumedItems.length > 3 ? ` y ${movement.consumedItems.length - 3} mas` : ''}
                            </p>
                          ) : null}
                          {!movement.active ? (
                            <p className="mt-1 text-[10px] font-bold text-rose-600">
                              Anulado el {movement.deletedAt ? formatDateTime(movement.deletedAt) : '-'} por {getUserLabel(profilesMap.get(movement.deletedBy))}.
                              {movement.deleteReason ? ` Motivo: ${movement.deleteReason}` : ''}
                            </p>
                          ) : null}
                        </div>

                        {movement.active ? (
                          <button
                            type="button"
                            onClick={() => {
                              if (movement.type === 'step') handleReverseStepEntry(movement.raw);
                              else handleReverseLegacyProduction(movement.raw);
                            }}
                            disabled={savingStep === movement.id}
                            className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-[9px] font-black uppercase tracking-widest text-rose-600 transition-all hover:bg-rose-100 disabled:opacity-60"
                          >
                            <Trash2 size={12} className="inline mr-1" />
                            Anular
                          </button>
                        ) : null}
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center text-[10px] font-bold text-slate-400">
                  Todavia no hay movimientos registrados para esta fecha.
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
